<template>
  <div class="content-alumni-profile">
    <div class="inner">
      <h2 class="title text-2xl">{{ t('title') }}</h2>
      <AlumniForm
        v-if="status !== 'error'"
        class="form"
        :member="member"
        :send-label="buttonLabel"
        :disabled="status !== 'success' || updatingStatus === 'pending'"
        :email-editable="false"
        :cannot-send-when-nothing-changed="true"
        :error="updatingStatus === 'error' ? t('error.updateFailed') : undefined"
        @submit="updateProfile($event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import AlumniForm, { type AlumniFormSubmitEventData } from '~/components/content-modules/alumni/AlumniForm.vue';
const { t } = useI18n();

const { data: member, status, refresh } = useFetch('/api/alumni/me', {});

const updatingStatus = ref<'idle' | 'pending' | 'success' | 'error'>('idle');

const buttonLabel = computed(() => {
  if (updatingStatus.value === 'pending') {
    return t('saving');
  }
  return t('submit');
});

async function updateProfile(event: AlumniFormSubmitEventData) {
  updatingStatus.value = 'pending';
  try {
    await $fetch('/api/alumni/update-profile', {
      method: 'POST',
      body: {
        contactDetails: {
          ...event.contactDetails,
        },
        customFields: {
          ...event.customFields,
        },
      },
    });

    if (event.profilePictureFormData) {
      await $fetch('/api/alumni/update-profile-picture', {
        method: 'POST',
        body: event.profilePictureFormData,
      });
    }

    updatingStatus.value = 'success';

    if (
      event.contactDetails.firstName !== member.value?.firstName ||
      event.contactDetails.familyName !== member.value?.familyName ||
      event.profilePictureFormData
    ) {
      // reload page when name or profile picture changed to update user nav
      window.location.reload();
    }
  } catch {
    updatingStatus.value = 'error';
  } finally {
    await refresh();
  }
}
</script>

<style scoped lang="scss">
.content-alumni-profile {
  @include grid-layout();

  & {
    padding: var(--site-inner-padding);
    width: 100%;
    max-width: var(--site-max-width);
  }

  > .inner {
    @media (--xs), (--sm) {
      padding: var(--spacing-sm) 0;
    }

    @media (--xs) {
      grid-column: 1 / -1;
    }

    @media (--sm) {
      grid-column: 2 / 24;
    }

    @media (--md), (--lg) {
      grid-column: 5 / 21;
      padding-bottom: var(--spacing-2xl);
    }
  }
}
</style>

<i18n lang="yaml">
de:
  title: Profil Informationen
  submit: Speichern
  saved: Gespeichert
  saving: Wird gespeichert...
  error:
    updateFailed: Aktualisierung des Profils ist fehlgeschlagen.

en:
  title: Profile Information
  submit: Save
  saved: Saved
  saving: Saving...
  error:
    updateFailed: Update of the profile failed.
</i18n>
